
import type {StrictUiEmitterEventMap} from '@gongxigames/framework/slotV2/globals/strictUiEmitter';
import {FeatureEventSpinGame, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {FlowModuleEvents, IEventManagerWithAsync, IService, LogicEvents, ReelsEvents, SystemComponentType} from '@gongxigames/framework/slot';

type OldToNewEventMapping<T> = {
    [K in keyof StrictUiEmitterEventMap]: {
        oldEvent: string | symbol;
        newEvent: K;
        args?: StrictUiEmitterEventMap[K];
        mapper?: (args: any) => FeatureEventSpinGame | symbol | void;
    };
}[keyof StrictUiEmitterEventMap];

/**
 * Event bridging service that forwards events between old EventManagerWithAsync and new strictUiEmitter
 */
@injectable()
export class EventBridge implements IService {
    public static readonly NAME = 'EventBridge';
    public type: SystemComponentType = 'service';

    private isProcessingEvent = new Set<string>();
    @inject(CoreDI.IEventManagerWithAsync)
    private oldEventManager!: IEventManagerWithAsync;

    private OLD_TO_NEW_EVENT_MAPPING: OldToNewEventMapping<keyof StrictUiEmitterEventMap>[] = [
        {oldEvent: 'General.Flow.PAID_ROUND_ENDED', newEvent: 'General.Flow.PAID_ROUND_ENDED'},
        {oldEvent: 'General.Flow.WAIT_EVENT_BEFORE_RESTORE_GAME', newEvent: 'General.Flow.WAIT_EVENT_BEFORE_RESTORE_GAME'},
        // {oldEvent: 'General.Flow.WAITING_OF_RESTORE_GAME', newEvent: 'General.Flow.WAITING_OF_RESTORE_GAME'}, // only listened in promotions
        // {oldEvent: 'General.Game.STARTED', newEvent: 'General.Game.STARTED'}, // only listened in promotions
        {oldEvent: 'General.Promotion.ActivateCounter', newEvent: 'General.Promotion.ActivateCounter'},
        {oldEvent: 'General.Promotion.DeactivateCounter', newEvent: 'General.Promotion.DeactivateCounter'},
        {oldEvent: 'General.Promotion.Ended', newEvent: 'General.Promotion.Ended'},
        {oldEvent: 'General.Promotion.Campaign.Ended', newEvent: 'General.Promotion.Campaign.Ended'},
        {oldEvent: 'General.Promotion.Started', newEvent: 'General.Promotion.Started'},
        {oldEvent: 'General.Promotion.UpdateCounter', newEvent: 'General.Promotion.UpdateCounter'},
        {oldEvent: 'MultiSetsMulticampaignPromotion.START_STATE_LEFT', newEvent: 'MultiSetsMulticampaignPromotion.START_STATE_LEFT'},
        {oldEvent: 'MultiSetsPromotion.START_STATE_LEFT', newEvent: 'MultiSetsPromotion.START_STATE_LEFT'},
        {oldEvent: LogicEvents.BALANCE_UPDATED, newEvent: 'LogicEvents.BALANCE_UPDATED'},
        {oldEvent: 'General.Slot.SPIN.DISABLE', newEvent: 'General.disableSpinButton'},
        // {oldEvent: 'General.Slot.SPIN.ENABLE', newEvent: 'General.enableSpinButton'}, // cause problem with jackpot mania and restore with promotions
        {oldEvent: ReelsEvents.REELS_SPINNING_STARTED, newEvent: 'General.Reels.StartSpinning'},
        {oldEvent: FlowModuleEvents.FEATURE_STARTED, newEvent: 'Game.command.round.featureAccepted'},
        {oldEvent: 'General.UI.DISABLE', newEvent: 'Game.command.round.uiDisabled', mapper: () => {}},
        {oldEvent: 'General.UI.ENABLE', newEvent: 'Game.command.round.uiEnabled', mapper: () => {}},
        // ------------
        // {oldEvent: 'General.Message.SHOW', newEvent: 'General.Message.SHOW' }, is used in slotv2, but not in strictUiEmitter should be added when appear in strictUiEmitter
        // {oldEvent: 'General.Popups.Add', newEvent: 'General.Popups.Add' }, // is used in slotv2, but not in strictUiEmitter should be added when appear in strictUiEmitter
        // {oldEvent: 'General.Popups.Remove', newEvent: 'General.Popups.Remove' }, // is used in slotv2, but not in strictUiEmitter should be added when appear in strictUiEmitter
        // {oldEvent: 'Autoplay.FORCE_STOP', newEvent: 'Autoplay.FORCE_STOP' },
        // {oldEvent: 'Autoplay.STOP', newEvent: 'Autoplay.STOP' },
        // {oldEvent: 'General.UI.Button.ENABLE', newEvent: 'General.UI.Button.ENABLE' }, // UIEvents.ts
        // {oldEvent: 'General.Window.GO_TO_HOME', newEvent: 'General.Window.GO_TO_HOME' }, no events in slotv2
        // {oldEvent: 'General.Window.RELOAD', newEvent: 'General.Window.RELOAD' }, // no events in slotv2
        // {oldEvent: LogicEvents.RESPONSE, newEvent: 'LogicEvents.RESPONSE' }, // no events in slotv2
        // {oldEvent: LogicEvents.ROUND_START, newEvent: 'LogicEvents.ROUND_START' },
        // {oldEvent: ReelsEvents.SPIN_ENDED, newEvent: 'ReelsEvents.SPIN_ENDED' }, NotifySpinEndAct.ts old event is there
        // {oldEvent: ReelsEvents.ALL_REELS_STOPPED, newEvent: 'ReelsEvents.ALL_REELS_STOPPED' }, NotifyAllReelsStoppedAct.ts old event is there
    ];

    private NEW_TO_OLD_EVENT_MAPPING: OldToNewEventMapping<symbol>[] = [
        // {oldEvent: 'General.Slot.SPIN.DISABLE', newEvent: 'General.disableSpinButton'},
        // {oldEvent: 'General.Slot.SPIN.ENABLE', newEvent: 'General.enableSpinButton'},
        // {oldEvent: 'General.UI.DISABLE', newEvent: 'Game.command.round.featureStarted', type: 'uiDisabled', mapper: (args: FeatureEventUiDisabled) => args.name!},
        // {oldEvent: 'General.UI.ENABLE', newEvent: 'Game.command.round.featureStopped', type: 'uiDisabled', mapper: (args: FeatureEventUiDisabled) => args.name!},
        {oldEvent: ReelsEvents.REELS_RESPINNING_STARTED, newEvent: 'General.Reels.Respin.StartSpinning'},
        {oldEvent: ReelsEvents.REELS_SPINNING_STARTED, newEvent: 'General.Reels.StartSpinning'},
        {oldEvent: 'General.Flow.WAITING_OF_RESTORE_GAME', newEvent: 'General.Flow.WAITING_OF_RESTORE_GAME'},
        {oldEvent: FlowModuleEvents.FEATURE_STARTED, newEvent: 'Game.command.round.featureAccepted'},
        // {oldEvent: 'General.Game.STARTED', newEvent: 'General.Game.STARTED'},
        // {oldEvent: 'General.Game.SYSTEM_INITIALIZED', newEvent: 'General.Game.SYSTEM_INITIALIZED'},
        {oldEvent: 'Autoplay.CHANGED_OPTION', newEvent: 'UI.command.autoplay.changedOption'},
        {oldEvent: 'Autoplay.START', newEvent: 'UI.command.startAutoplay'},
    ];

    init(): void {
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.OLD_TO_NEW_EVENT_MAPPING.forEach((mapping) => {
            const oldEventKey = mapping.oldEvent;

            this.oldEventManager.on(
                oldEventKey,
                (args: any) => {
                    this.forwardFromOldToNew(oldEventKey, mapping.mapper ? mapping.mapper(args) : args);
                },
                this,
            );
        });

        this.NEW_TO_OLD_EVENT_MAPPING.forEach((mapping) => {
            const newEventKey = mapping.newEvent;

            strictUiEmitter.on(
                newEventKey,
                (args: typeof mapping.args) => {
                    this.forwardFromNewToOld(newEventKey, mapping.mapper ? mapping.mapper(args) : args);
                });
        });
    }

    private checkForNewEvent(newEventKey: keyof StrictUiEmitterEventMap): boolean {
        return this.NEW_TO_OLD_EVENT_MAPPING.some((mapping) => mapping.newEvent === newEventKey);
    }

    private checkForOldEvent(oldEventKey: string | symbol): boolean {
        return this.OLD_TO_NEW_EVENT_MAPPING.some((mapping) => mapping.oldEvent === oldEventKey);
    }

    private forwardFromOldToNew(oldEventKey: string | symbol, args: StrictUiEmitterEventMap[keyof StrictUiEmitterEventMap]): void {
        const eventId = String(oldEventKey);
        const newEvent = this.OLD_TO_NEW_EVENT_MAPPING.find((mapping) => mapping.oldEvent === oldEventKey);
        // const argsHash = `${JSON.stringify(typeof args === 'symbol' ? String(args) : JSON.stringify(args))}`;
        // const oldHash = `${eventId}-${argsHash}`;
        // const newHash = `${newEvent?.newEvent}-${argsHash}`;

        if (!newEvent) {
            return;
        }

        const newEventKey = newEvent.newEvent;

        if (this.isProcessingEvent.has(newEventKey)) {
            this.isProcessingEvent.delete(newEventKey);

            return;
        }

        // check if new event exist in NEW_TO_OLD_EVENT_MAPPING to prevent infinite loop
        if (this.checkForNewEvent(newEventKey)) {
            this.isProcessingEvent.add(eventId);
        }

        strictUiEmitter.emit(newEventKey, args);
    }

    private forwardFromNewToOld(newEventKey: keyof StrictUiEmitterEventMap, args: StrictUiEmitterEventMap[typeof newEventKey]): void {
        const eventId = `${newEventKey}`;
        const oldEvent = this.NEW_TO_OLD_EVENT_MAPPING.find((mapping) => mapping.newEvent === newEventKey);

        // const argsHash = `${JSON.stringify(typeof args === 'symbol' ? String(args) : JSON.stringify(args))}`;
        // const oldHash = `${String(oldEvent?.oldEvent)}-${argsHash}`;
        // const newHash = `${newEventKey}-${argsHash}`;

        if (!oldEvent) {
            return;
        }

        const oldEventKey = oldEvent.oldEvent;

        if (this.isProcessingEvent.has(String(oldEventKey))) {
            this.isProcessingEvent.delete(String(oldEventKey));

            return;
        }

        // check if old event exist in OLD_TO_NEW_EVENT_MAPPING to prevent infinite loop
        if (this.checkForOldEvent(oldEventKey)) {
            this.isProcessingEvent.add(eventId);
        }

        this.oldEventManager.emit(oldEvent.oldEvent, args);

    }

    destroy(): void {
        this.isProcessingEvent.clear();
    }
}
